import { adminSupabase, supabase, WHATSAPP_SERVICE_PRODUCT_ID } from '@/lib/supabase'

export type SubscriptionPlan = 'basic' | 'pro' | 'enterprise'
export type SubscriptionStatus = 'active' | 'cancelled' | 'expired' | 'trial'

export interface CentralSubscription {
  org_id: string
  organization_name: string
  user_email: string
  plan: SubscriptionPlan
  status: SubscriptionStatus
  product_id: string
  started_at: string
  expires_at?: string | null
}

export async function listWhatsAppSubscriptions(): Promise<CentralSubscription[]> {
  const productId = WHATSAPP_SERVICE_PRODUCT_ID
  const { data, error } = await adminSupabase
    .from('central_subscriptions')
    .select('org_id, organization_name, user_email, plan, status, product_id, started_at, expires_at')
    .eq('product_id', productId)
    .order('started_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as CentralSubscription[]
}

export async function addWhatsAppSubscription(
  payload: Pick<CentralSubscription, 'org_id' | 'organization_name' | 'user_email' | 'plan'> & {
    expires_at?: string | null
    status?: SubscriptionStatus
  }
): Promise<void> {
  const productId = WHATSAPP_SERVICE_PRODUCT_ID
  const { error } = await adminSupabase.from('central_subscriptions').insert({
    org_id: payload.org_id,
    organization_name: payload.organization_name,
    user_email: payload.user_email,
    plan: payload.plan,
    status: payload.status ?? 'active',
    product_id: productId,
    expires_at: payload.expires_at ?? null,
  })
  if (error) throw error
}

export async function updateWhatsAppSubscription(
  orgId: string,
  payload: Pick<CentralSubscription, 'organization_name' | 'user_email' | 'plan' | 'status'> & {
    expires_at?: string | null
  }
): Promise<void> {
  const { error } = await adminSupabase
    .from('central_subscriptions')
    .update({
      organization_name: payload.organization_name,
      user_email: payload.user_email,
      plan: payload.plan,
      status: payload.status,
      expires_at: payload.expires_at ?? null,
    })
    .eq('org_id', orgId)

  if (error) throw error
}

export async function cancelWhatsAppSubscription(orgId: string): Promise<void> {
  const { error } = await adminSupabase
    .from('central_subscriptions')
    .update({ status: 'cancelled' })
    .eq('org_id', orgId)

  if (error) throw error
}

export type DeviceBillingStatus = 'included' | 'extra_billed' | 'free' | 'comped'

/** Jumpstart Hub rows from `whatsapp_devices` (session_key = org_id on VPS). */
export interface WhatsappHubDevice {
  id: string
  org_id: string
  session_key?: string | null
  user_email: string
  label: string
  phone_number: string | null
  meta_phone_number_id?: string | null
  meta_waba_id?: string | null
  phone_number_id?: string | null
  provider?: 'baileys' | 'meta-cloud' | null
  created_at: string
  price_agorot: number
  billing_status: DeviceBillingStatus
  notes: string | null
}

/** @deprecated Use WhatsappHubDevice */
export type WhatsappDeviceRow = WhatsappHubDevice

export async function listWhatsappHubDevices(): Promise<WhatsappHubDevice[]> {
  const { data, error } = await adminSupabase
    .from('whatsapp_devices')
    .select('id, org_id, session_key, user_email, label, phone_number, meta_phone_number_id, meta_waba_id, phone_number_id, provider, created_at, price_agorot, billing_status, notes')
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data ?? []).map((row: Record<string, unknown>) => ({
    ...(row as unknown as WhatsappHubDevice),
    price_agorot: typeof row.price_agorot === 'number' ? row.price_agorot : 0,
    billing_status: (row.billing_status as DeviceBillingStatus) ?? 'included',
    notes: row.notes != null ? String(row.notes) : null,
  }))
}

export async function countWhatsappDevicesForOrg(orgId: string): Promise<number> {
  const { count, error } = await adminSupabase
    .from('whatsapp_devices')
    .select('id', { count: 'exact', head: true })
    .eq('org_id', orgId)

  if (error) throw error
  return count ?? 0
}

/** Per-device monthly billing breakdown for org Hub devices (Otomator DB). */
export async function getWhatsappDeviceMonthlyBillingForOrg(orgId: string): Promise<{
  deviceCount: number
  totalPriceAgorot: number
  extraBilledCount: number
  extraBilledSumAgorot: number
}> {
  const { data, error } = await adminSupabase
    .from('whatsapp_devices')
    .select('price_agorot, billing_status')
    .eq('org_id', orgId)

  if (error) throw error
  const rows = data ?? []
  let totalPriceAgorot = 0
  let extraBilledCount = 0
  let extraBilledSumAgorot = 0
  for (const r of rows) {
    const p = typeof r.price_agorot === 'number' ? r.price_agorot : 0
    totalPriceAgorot += p
    if (r.billing_status === 'extra_billed') {
      extraBilledCount += 1
      extraBilledSumAgorot += p
    }
  }
  return {
    deviceCount: rows.length,
    totalPriceAgorot,
    extraBilledCount,
    extraBilledSumAgorot,
  }
}

export async function createHubDevice(params: {
  orgId: string
  label: string
  provider: 'baileys' | 'meta-cloud'
  phoneNumber?: string | null
  billingStatus: DeviceBillingStatus
  priceAgorot: number
  notes?: string | null
  userEmail: string
}): Promise<void> {
  const { error } = await adminSupabase.from('whatsapp_devices').insert({
    org_id: params.orgId,
    session_key: params.orgId,
    user_email: params.userEmail,
    label: params.label.trim(),
    phone_number: params.phoneNumber?.trim() || null,
    provider: params.provider,
    price_agorot: params.billingStatus === 'extra_billed' ? params.priceAgorot : 0,
    billing_status: params.billingStatus,
    notes: params.notes?.trim() || null,
  })
  if (error) throw error
}

export async function saveMetaCredentials(params: {
  orgId: string
  phoneNumberId: string
  wabaId: string
  accessToken: string
}): Promise<void> {
  // Update Otomator whatsapp_devices
  const { error: devErr } = await adminSupabase
    .from('whatsapp_devices')
    .update({
      meta_phone_number_id: params.phoneNumberId,
      meta_waba_id: params.wabaId,
      phone_number_id: params.phoneNumberId,
    })
    .eq('org_id', params.orgId)
    .eq('provider', 'meta-cloud')
  if (devErr) throw devErr

  // Update JumpStart whatsapp_connections
  const { error: connErr } = await supabase
    .from('whatsapp_connections')
    .update({
      phone_number_id: params.phoneNumberId,
      waba_id: params.wabaId,
      access_token: params.accessToken,
    })
    .eq('org_id', params.orgId)
    .eq('method', 'official')
  if (connErr) throw connErr
}

export async function metaPhoneRegisterAction(params: {
  action: 'check_status' | 'request_code' | 'verify_code' | 'register' | 'save_device'
  phone_number_id?: string
  code_method?: 'SMS' | 'VOICE'
  code?: string
  pin?: string
  org_id?: string
  user_email?: string
  label?: string
  phone_number?: string
}): Promise<unknown> {
  const { data, error } = await adminSupabase.functions.invoke('meta-phone-register', {
    body: params,
  })
  if (error) throw error
  return data
}
